/**
 * Family profile list for the account screen.
 * Tapping a row opens the profile edit flow at /profile/[pipedriveId].
 */
import { router } from "expo-router";
import { useTranslation } from "react-i18next";
import { View } from "react-native";

import { Text } from "@/components/ui/text";
import { useAuth } from "@/lib/auth";
import { useGetProfilesQuery } from "@/query/apiSlice";

import { ListSection } from "@/components/ui/list-section";
import { Section } from "@/components/ui/section";
import { getProfileLabel } from "../lib/formatProfileSummary";
import { FamilyProfileRow } from "./FamilyProfileRow";

export function FamilyProfilesSection() {
    const { t } = useTranslation();
    const { authContext } = useAuth();
    const parentPipedriveId = authContext?.pipedrive_id ?? null;
    const { data: profiles, isLoading } = useGetProfilesQuery();

    const sortedProfiles = [...(profiles ?? [])].sort((left, right) => {
        return getProfileLabel(left).localeCompare(getProfileLabel(right));
    });

    if (isLoading) {
        return (
            <View className="px-4">
                <Text variant="muted">{t("profile.loadingProfiles")}</Text>
            </View>
        );
    }

    if (sortedProfiles.length === 0) {
        return (
            <Section
                title={t("profile.familyProfiles")}
                footer={t("profile.familyProfilesEmptyFooter")}
            >
                <Text variant="muted" className="p-4">
                    {t("profile.error.noProfiles")}
                </Text>
            </Section>
        );
    }

    return ["parent", "student", "other"].map((role) => {
        const items = sortedProfiles.filter((profile) => profile.role === role);
        if (items.length === 0) {
            return null;
        }
        return (
            <ListSection
                key={`role-${role}`}
                title={t(
                    `profile.roles.${role}${items.length > 1 ? "Plural" : ""}`,
                )}
            >
                {sortedProfiles
                    .filter((profile) => profile.role === role)
                    .map((profile) => (
                        <FamilyProfileRow
                            key={profile.id}
                            profile={profile}
                            badge={
                                parentPipedriveId != null &&
                                profile.id === parentPipedriveId
                                    ? t("profile.badge.you")
                                    : null
                            }
                            onPress={() => {
                                router.push(`/profile/${profile.id}`);
                            }}
                        />
                    ))}
            </ListSection>
        );
    });
}
